import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { api, type Product } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
    ArrowLeft, ShoppingBag, Minus, Plus, X, User, Phone,
    CheckCircle, Truck, Shield, Star, Heart, Share2, Loader2
} from "lucide-react"
import { useLanguage } from "@/lib/language-context"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"

export default function ProductDetailPage() {
    const { slug } = useParams<{ slug: string }>()
    const [product, setProduct] = useState<Product | null>(null)
    const [loading, setLoading] = useState(true)
    const [quantity, setQuantity] = useState(1)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [customerName, setCustomerName] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [isFavorite, setIsFavorite] = useState(false)
    const { t } = useLanguage()

    useEffect(() => {
        const fetchProduct = async () => {
            if (!slug) return
            try {
                setLoading(true)
                const prod = await api.getProduct(slug)
                setProduct(prod)
            } catch (error) {
                console.log("Error fetching product:", error)
            } finally {
                setLoading(false)
            }
        }

        fetchProduct()
    }, [slug])

    // Format phone number as XX-XXX-XX-XX
    const formatPhoneNumber = (value: string) => {
        const digits = value.replace(/\D/g, "")
        const limited = digits.slice(0, 9)

        let formatted = ""
        if (limited.length > 0) formatted = limited.slice(0, 2)
        if (limited.length > 2) formatted += "-" + limited.slice(2, 5)
        if (limited.length > 5) formatted += "-" + limited.slice(5, 7)
        if (limited.length > 7) formatted += "-" + limited.slice(7, 9)

        return formatted
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatPhoneNumber(e.target.value)
        setPhoneNumber(formatted)
    }

    const handleBuyClick = () => {
        setIsModalOpen(true)
        setIsSuccess(false)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setCustomerName("")
        setPhoneNumber("")
        setIsSuccess(false)
    }

    const handleSubmitOrder = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!customerName.trim() || phoneNumber.replace(/-/g, "").length !== 9) {
            return
        }

        if (!product) return

        setIsSubmitting(true)

        try {
            const result = await api.createOrder({
                product: product.id,
                customer_name: customerName,
                phone_number: "+998" + phoneNumber.replace(/-/g, ""),
                quantity: quantity,
                note: ""
            })

            if (result.success) {
                setIsSuccess(true)
                setTimeout(() => {
                    handleCloseModal()
                }, 2500)
            } else {
                alert(result.error || "Xatolik yuz berdi. Iltimos, qaytadan urinib ko'ring.")
            }
        } catch (error) {
            console.error("Error submitting order:", error)
            alert("Xatolik yuz berdi. Iltimos, qaytadan urinib ko'ring.")
        } finally {
            setIsSubmitting(false)
        }
    }

    const formatPrice = (price: string | number) => {
        return Number(price).toLocaleString("uz-UZ")
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Yuklanmoqda...</p>
                </div>
            </div>
        )
    }

    if (!product) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900">
                <div className="w-24 h-24 bg-slate-200 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <ShoppingBag className="h-12 w-12 text-muted-foreground" />
                </div>
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Mahsulot topilmadi</h2>
                    <p className="text-muted-foreground">Bu mahsulot mavjud emas yoki o'chirilgan</p>
                </div>
                <Link to="/products">
                    <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Mahsulotlarga qaytish
                    </Button>
                </Link>
            </div>
        )
    }

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
                {/* Breadcrumb */}
                <div className="container mx-auto px-4 pt-6">
                    <Link
                        to="/products"
                        className="inline-flex items-center gap-2 text-muted-foreground hover:text-emerald-600 transition-colors group"
                    >
                        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                        <span>Mahsulotlarga qaytish</span>
                    </Link>
                </div>

                <div className="container mx-auto px-4 py-8">
                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Product Image */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                            className="relative"
                        >
                            <div className="aspect-square rounded-3xl overflow-hidden bg-white dark:bg-gray-800 shadow-2xl shadow-slate-200 dark:shadow-none">
                                <img
                                    src={product.image_url || product.image || "/placeholder.svg"}
                                    alt={product.name}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            {/* Favorite & Share buttons */}
                            <div className="absolute top-4 right-4 flex gap-2">
                                <button
                                    onClick={() => setIsFavorite(!isFavorite)}
                                    className={cn(
                                        "w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-md transition-all",
                                        isFavorite
                                            ? "bg-red-500 text-white"
                                            : "bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 hover:bg-red-50 hover:text-red-500"
                                    )}
                                >
                                    <Heart className={cn("h-5 w-5", isFavorite && "fill-current")} />
                                </button>
                                <button className="w-12 h-12 rounded-full bg-white/80 dark:bg-gray-800/80 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:bg-emerald-50 hover:text-emerald-500 backdrop-blur-md transition-colors">
                                    <Share2 className="h-5 w-5" />
                                </button>
                            </div>
                            {/* Badges */}
                            {product.available && (
                                <Badge className="absolute top-4 left-4 bg-emerald-500 text-white border-0">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Mavjud
                                </Badge>
                            )}
                        </motion.div>

                        {/* Product Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="flex flex-col"
                        >
                            {/* Category */}
                            <div className="flex items-center gap-2 mb-4">
                                <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20">
                                    {product.category_name}
                                </Badge>
                                <div className="flex items-center gap-1 text-amber-500">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="h-4 w-4 fill-current" />
                                    ))}
                                    <span className="text-sm text-muted-foreground ml-2">(4.9)</span>
                                </div>
                            </div>

                            {/* Title */}
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                                {product.name}
                            </h1>

                            {/* Price */}
                            <div className="flex items-baseline gap-3 mb-6">
                                <span className="text-4xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                                    {formatPrice(product.price)}
                                </span>
                                <span className="text-xl text-muted-foreground">so'm</span>
                            </div>

                            {/* Description */}
                            <p className="text-muted-foreground leading-relaxed mb-8 whitespace-pre-line">
                                {product.description}
                            </p>

                            {/* Features */}
                            <div className="grid grid-cols-3 gap-4 mb-8">
                                <div className="flex flex-col items-center p-4 bg-slate-50 dark:bg-gray-800 rounded-xl">
                                    <Truck className="h-6 w-6 text-emerald-500 mb-2" />
                                    <span className="text-xs text-muted-foreground text-center">Bepul yetkazib berish</span>
                                </div>
                                <div className="flex flex-col items-center p-4 bg-slate-50 dark:bg-gray-800 rounded-xl">
                                    <Shield className="h-6 w-6 text-emerald-500 mb-2" />
                                    <span className="text-xs text-muted-foreground text-center">Sifat kafolati</span>
                                </div>
                                <div className="flex flex-col items-center p-4 bg-slate-50 dark:bg-gray-800 rounded-xl">
                                    <CheckCircle className="h-6 w-6 text-emerald-500 mb-2" />
                                    <span className="text-xs text-muted-foreground text-center">Tabiiy mahsulot</span>
                                </div>
                            </div>

                            {/* Quantity & Stock */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8">
                                <div className="flex items-center gap-4">
                                    <span className="text-sm font-medium">Miqdor:</span>
                                    <div className="flex items-center gap-2 bg-slate-100 dark:bg-gray-800 rounded-xl p-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-10 w-10 rounded-lg"
                                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        >
                                            <Minus className="h-4 w-4" />
                                        </Button>
                                        <span className="w-12 text-center font-semibold text-lg">{quantity}</span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-10 w-10 rounded-lg"
                                            onClick={() => setQuantity(quantity + 1)}
                                        >
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {product.available ? (
                                        <span className="flex items-center gap-2 text-emerald-600">
                                            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                                            Omborda: {product.stock} dona
                                        </span>
                                    ) : (
                                        <span className="text-red-500">Mavjud emas</span>
                                    )}
                                </div>
                            </div>

                            {/* Total Price */}
                            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl mb-6">
                                <span className="text-muted-foreground">Jami:</span>
                                <span className="text-2xl font-bold text-emerald-600">
                                    {formatPrice(Number(product.price) * quantity)} so'm
                                </span>
                            </div>

                            {/* Buy Button */}
                            <Button
                                size="lg"
                                className="w-full h-14 text-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/30 rounded-xl"
                                onClick={handleBuyClick}
                                disabled={!product.available}
                            >
                                <ShoppingBag className="mr-3 h-6 w-6" />
                                Sotib olish
                            </Button>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Order Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    >
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                            onClick={handleCloseModal}
                        />

                        {/* Modal Content */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
                        >
                            {isSuccess ? (
                                /* Success State */
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="p-10 flex flex-col items-center justify-center text-center"
                                >
                                    <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-500/40">
                                        <CheckCircle className="h-12 w-12 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                        Buyurtma qabul qilindi!
                                    </h3>
                                    <p className="text-muted-foreground">
                                        Tez orada siz bilan bog'lanamiz
                                    </p>
                                </motion.div>
                            ) : (
                                <>
                                    {/* Header */}
                                    <div className="bg-gradient-to-br from-emerald-500 to-teal-600 px-6 py-6">
                                        <button
                                            onClick={handleCloseModal}
                                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>
                                        <h2 className="text-2xl font-bold text-white mb-1">Buyurtma berish</h2>
                                        <p className="text-emerald-100 text-sm">Ma'lumotlaringizni kiriting</p>
                                    </div>

                                    {/* Product Summary */}
                                    <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-800">
                                        <div className="flex gap-4">
                                            <img
                                                src={product.image_url || product.image || "/placeholder.svg"}
                                                alt={product.name}
                                                className="w-20 h-20 rounded-xl object-cover"
                                            />
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1">{product.name}</h3>
                                                <p className="text-sm text-muted-foreground">Miqdor: {quantity} dona</p>
                                                <p className="text-lg font-bold text-emerald-600 mt-1">
                                                    {formatPrice(Number(product.price) * quantity)} so'm
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Form */}
                                    <form onSubmit={handleSubmitOrder} className="p-6 space-y-5">
                                        {/* Name Field */}
                                        <div>
                                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                                Ismingiz
                                            </label>
                                            <div className="relative">
                                                <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                                <input
                                                    type="text"
                                                    value={customerName}
                                                    onChange={(e) => setCustomerName(e.target.value)}
                                                    placeholder="Ismingizni kiriting"
                                                    className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-gray-800 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-gray-900 dark:text-white"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Phone Field */}
                                        <div>
                                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                                                Telefon raqam
                                            </label>
                                            <div className="relative">
                                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                                <span className="absolute left-12 top-1/2 -translate-y-1/2 text-gray-500 font-medium">+998</span>
                                                <input
                                                    type="tel"
                                                    value={phoneNumber}
                                                    onChange={handlePhoneChange}
                                                    placeholder="XX-XXX-XX-XX"
                                                    className="w-full pl-24 pr-4 py-4 bg-slate-50 dark:bg-gray-800 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-gray-900 dark:text-white tracking-wide"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <Button
                                            type="submit"
                                            size="lg"
                                            className="w-full h-14 text-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-xl shadow-lg shadow-emerald-500/30"
                                            disabled={isSubmitting || !customerName.trim() || phoneNumber.replace(/-/g, "").length !== 9}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                    Yuborilmoqda...
                                                </>
                                            ) : (
                                                <>
                                                    <ShoppingBag className="mr-2 h-5 w-5" />
                                                    Buyurtma berish
                                                </>
                                            )}
                                        </Button>
                                    </form>
                                </>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}
